import type { Project } from "../api/types";

/**
 * Batch 18 GRF-47 (ruling R3 A, B18-UX-SPECS §4): the generated project icon's colour and text, and the picker's search.
 * All eight colours give white text at least 6:1 (pinned by projectIcon.test.ts).
 */
export const PROJECT_ICON_COLOURS = ["#1d3496", "#7a2e8c", "#0f6e5c", "#a3401c", "#355e1f", "#8a1f3d", "#4b4f58", "#6b4a12"] as const;

/** FNV-1a over the key's characters (keys are ASCII): the same key always gives the same colour, on every page and after reload. */
export function projectIconColour(key: string): string {
  let hash = 0x811c9dc5;
  for (const char of key.toUpperCase()) {
    hash ^= char.charCodeAt(0);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return PROJECT_ICON_COLOURS[hash % PROJECT_ICON_COLOURS.length] ?? PROJECT_ICON_COLOURS[0];
}

/** A key of 1–3 characters shows whole ("EVP"); a longer one its first two characters ("PATPMS" → "PA"). */
export function projectIconText(key: string): string {
  const chars = Array.from(key.toUpperCase());
  return chars.length <= 3 ? chars.join("") : chars.slice(0, 2).join("");
}

/** Read-only, closed and archived projects: the icon at half opacity, and these words beside it (never colour alone). */
export function projectStateWords(lifecycle: Project["lifecycle"]): string | null {
  if (lifecycle === "read_only") return "Read-only";
  if (lifecycle === "closed") return "Closed";
  if (lifecycle === "archived") return "Archived";
  return null;
}

/** More projects than this and the picker gets a "Find a project" field (B18-UX-SPECS §4.6). */
export const PROJECT_SEARCH_THRESHOLD = 8;

const fold = (text: string) => text.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/** Case- and Latin-diacritic-insensitive; Thai as written (tone marks kept, they change the word), as the person bar's search. */
export function foldForSearch(text: string): string {
  return fold(text);
}

/**
 * "Find a project": the key or the name contains the text. Projects whose key starts with the text come first, then the
 * rest in their own order, so typing "EV" puts EVP at the top.
 */
export function filterProjects<T extends Pick<Project, "key" | "name">>(projects: readonly T[], query: string): T[] {
  const needle = fold(query.trim());
  if (!needle) return [...projects];
  const matches = projects.filter((p) => fold(p.key).includes(needle) || fold(p.name).includes(needle));
  return [...matches.filter((p) => fold(p.key).startsWith(needle)), ...matches.filter((p) => !fold(p.key).startsWith(needle))];
}
